import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Select, Tabs } from '@artosera/ui';
import { partiesLocales } from '@artosera/shared';
import {
  useReservationsRdv,
  useReglagesRdv,
  useTypesRdv,
  type VueRdv,
} from '../../api/priseRendezVous.js';
import { useEquipe } from '../../api/equipe.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { ReservationPanneau } from './ReservationPanneau.js';
import { ErreurRdv } from './ErreurRdv.js';
import { dateHeureRdv } from './temps.js';
import styles from './RendezVous.module.css';

export function ReservationsPage() {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [params, setParams] = useSearchParams();
  const [vue, setVue] = useState<VueRdv>('a_valider');
  const [typeId, setTypeId] = useState('');
  const [membreId, setMembreId] = useState('');
  const [page, setPage] = useState(1);
  const [copie, setCopie] = useState<'ok' | 'erreur' | null>(null);
  const lire =
    galerieAModule(galerie, 'prise-rendez-vous') && galerieALaction(galerie, 'creneaux:read');
  const liste = useReservationsRdv(galerieSlug, { vue, typeId, membreId, page }, lire);
  const types = useTypesRdv(galerieSlug, lire);
  const reglages = useReglagesRdv(galerieSlug, lire);
  const equipe = useEquipe(galerieSlug, {
    enabled: lire && galerieALaction(galerie, 'memberships:read'),
  });
  const fuseau = reglages.data?.reglages.fuseau;
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const membres = new Map(equipe.data?.membres.map((m) => [m.compteId, m.nom]) ?? []);
  for (const r of liste.data?.reservations ?? [])
    membres.set(r.membreId, r.membreNom || r.membreId);
  const jours = new Map<string, NonNullable<typeof liste.data>['reservations']>();
  if (fuseau)
    for (const r of liste.data?.reservations ?? []) {
      const jour = partiesLocales(new Date(r.debut), fuseau).date;
      jours.set(jour, [...(jours.get(jour) ?? []), r]);
    }
  if (!lire) return <p role="alert">{t('regie:priseRendezVous.interdit')}</p>;
  const contenu = (
    <div className={styles.formulaire}>
      {liste.isLoading || reglages.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
      <ErreurRdv erreur={liste.error ?? reglages.error ?? types.error ?? equipe.error} />
      {fuseau && !liste.isLoading && !liste.isError && !jours.size ? (
        <p>{t('regie:priseRendezVous.vide')}</p>
      ) : null}
      {[...jours].map(([jour, reservations]) => (
        <section key={jour} className={styles.jour} aria-label={jour}>
          <h2>{jour}</h2>
          <ul className={styles.liste}>
            {reservations.map((r) => (
              <li key={r.id} className={styles.carte}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    const suite = new URLSearchParams(params);
                    suite.set('reservation', r.id);
                    setParams(suite);
                  }}
                >
                  {r.visiteur.nom || t('regie:priseRendezVous.contactEfface')} · {r.numero}
                </Button>
                <p>
                  {dateHeureRdv(r.debut, fuseau!)} –{' '}
                  {partiesLocales(new Date(r.fin), fuseau!).heure} ·{' '}
                  {r.typeTitre[langue] || r.typeTitre.fr}
                </p>
                <p>
                  {r.oeuvreTitre} · {r.membreNom}
                </p>
                <Badge>{t(`regie:priseRendezVous.etats.${r.etat}`)}</Badge>
                {r.agendaModifie ? <p>{t('regie:priseRendezVous.agendaModifie')}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {liste.data && liste.data.pagination.total > liste.data.pagination.parPage ? (
        <nav className={styles.actions} aria-label={t('regie:priseRendezVous.pagination')}>
          <Button variant="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
            {t('regie:priseRendezVous.precedent')}
          </Button>
          <span>
            {page} / {Math.ceil(liste.data.pagination.total / liste.data.pagination.parPage)}
          </span>
          <Button
            variant="secondary"
            disabled={page * liste.data.pagination.parPage >= liste.data.pagination.total}
            onClick={() => setPage(page + 1)}
          >
            {t('regie:priseRendezVous.suivant')}
          </Button>
        </nav>
      ) : null}
    </div>
  );
  return (
    <section className={styles.page}>
      <header className={styles.entete}>
        <h1>{t('regie:priseRendezVous.titre')}</h1>
        <Badge>{t('regie:priseRendezVous.aValider', { count: liste.data?.aValider ?? 0 })}</Badge>
        {galerieALaction(galerie, 'creneaux:configurer') ? (
          <Link to={`/${galerieSlug}/reglages/rendez-vous-en-ligne`}>
            {t('regie:priseRendezVous.reglages')}
          </Link>
        ) : null}
      </header>
      {liste.data ? (
        <div className={styles.bloc}>
          <a href={liste.data.lienPublic} target="_blank" rel="noreferrer">
            {liste.data.lienPublic}
          </a>
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(liste.data!.lienPublic);
                setCopie('ok');
              } catch {
                setCopie('erreur');
              }
            }}
          >
            {t('regie:priseRendezVous.copier')}
          </Button>
          {copie ? (
            <p role={copie === 'ok' ? 'status' : 'alert'}>
              {t(
                copie === 'ok'
                  ? 'regie:priseRendezVous.copie'
                  : 'regie:priseRendezVous.copieErreur',
              )}
            </p>
          ) : null}
        </div>
      ) : null}
      <p>{fuseau}</p>
      <div className={styles.grille} role="search" aria-label={t('regie:priseRendezVous.filtres')}>
        <Field htmlFor="rdv-type" label={t('regie:priseRendezVous.type')}>
          <Select
            id="rdv-type"
            value={typeId || 'tous'}
            onValueChange={(v) => {
              setTypeId(v === 'tous' ? '' : v);
              setPage(1);
            }}
            options={[
              { value: 'tous', label: t('regie:priseRendezVous.tous') },
              ...(types.data?.types.map((v) => ({
                value: v.id,
                label: v.titre[langue] || v.titre.fr,
              })) ?? []),
            ]}
          />
        </Field>
        <Field htmlFor="rdv-membre" label={t('regie:priseRendezVous.membre')}>
          <Select
            id="rdv-membre"
            value={membreId || 'tous'}
            onValueChange={(v) => {
              setMembreId(v === 'tous' ? '' : v);
              setPage(1);
            }}
            options={[
              { value: 'tous', label: t('regie:priseRendezVous.tous') },
              ...[...membres].map(([value, label]) => ({ value, label })),
            ]}
          />
        </Field>
      </div>
      <Tabs
        value={vue}
        onValueChange={(v) => {
          setVue(v as VueRdv);
          setPage(1);
        }}
        tabs={(['a_valider', 'a_venir', 'passees', 'closes'] as const).map((v) => ({
          value: v,
          label: t(`regie:priseRendezVous.vues.${v}`),
          content: contenu,
        }))}
      />
      {params.get('reservation') && fuseau ? (
        <ReservationPanneau
          key={params.get('reservation')}
          id={params.get('reservation')!}
          fuseau={fuseau}
          onFermer={() => {
            const suite = new URLSearchParams(params);
            suite.delete('reservation');
            setParams(suite);
          }}
        />
      ) : null}
    </section>
  );
}
